import React, { FC, useContext, useEffect, useState } from 'react';

import { Paper } from '@material-ui/core';
import Zoom from '@material-ui/core/Zoom';
import EditIcon from '@material-ui/icons/Edit';
import ReactMarkdown from 'react-markdown';
import RichTextEditor, { EditorValue } from 'react-rte';

import { GlobalContext } from '../../../../../../store';
import { IAdminClub } from '../../../../../../store/admin/clubs/store';

import Button from '../../../../../../components/Button';
import { FormConsumer, FormProvider, IFormAttributes } from '../../../../../../components/Forms';
import { ImagePicker } from '../../../../../../components/ImagePicker/ImagePicker';
import Tooltip from '../../../../../../components/Tooltip';
import { RTE_TOOLBARCONFIG } from '../../Utils/RTE_TOOLBARCONFIG';
import { useRouteInfoState } from '../../../../../../components/RouteInfo/Context/RouteInfo.context';

const AboutTheClub: FC = () => {
  const {
    actions,
    actions: {
      notifications: { pushSuccess, pushError }
    },
    adminClubs
  } = useContext(GlobalContext);

  const { clubId } = useRouteInfoState();

  const [currentClub, setCurrentClub] = useState<IAdminClub>();

  const [text, setText] = useState<EditorValue>(RichTextEditor.createEmptyValue());

  const [formAboutDisabled, setFormAboutDisabled] = useState(true);

  useEffect(() => {
    if (clubId) {
      setCurrentClub(adminClubs[Number(clubId)]);
    }
  }, [adminClubs, clubId]);

  useEffect(() => {
    if (currentClub) {
      setText(
        currentClub.description
          ? RichTextEditor.createValueFromString(currentClub.description, 'markdown')
          : RichTextEditor.createEmptyValue()
      );
    }
  }, [currentClub]);

  const onSubmitAboutSettings = async (data: any) => {
    const regex = /data:image\/([a-zA-Z]+);/;
    let imageUpdateResponse;

    const body: any = {
      description: data.description
    };

    const response = await actions.adminClubs.edit(clubId, body);
    if (data.backgroundImage && data.backgroundImage.match(regex)) {
      imageUpdateResponse = await actions.adminClubs.editImage(
        clubId,
        data.backgroundImage,
        'background_image'
      );
    }
    if (response.errors || (!!imageUpdateResponse && imageUpdateResponse.errors)) {
      pushError('There was an error updating the club settings');
    } else {
      pushSuccess('Club settings updated successfully!');
    }
    setFormAboutDisabled(true);
  };

  const toggleForm = () => {
    setFormAboutDisabled(!formAboutDisabled);
  };

  return (
    <Paper elevation={1} className="paper" id="about-settings-paper">
      {!!currentClub && (
        <FormProvider
          defaultValues={currentClub}
          onSubmit={onSubmitAboutSettings}
          validations={{
            backgroundImage: 'required',
            description: 'required'
          }}
        >
          <FormConsumer>
            {({ values, formActions: { setValue } }: IFormAttributes) => (
              <div>
                <div className="header-card-container">
                  <div>
                    <span className="header-title">ABOUT THE CLUB</span>

                    <Tooltip
                      classes={{ fab: 'tooltip-fab--shift-top-3' }}
                      sectionKey="CLUB_SETTINGS.ABOUT_CLUB"
                    />
                  </div>

                  <div className="buttons-toggle-zoom">
                    <Zoom in={formAboutDisabled}>
                      <Button
                        onClick={toggleForm}
                        variant="text"
                        color="primary"
                        className="button edit-club-about-button"
                      >
                        <div className="icon-container">
                          <EditIcon className="edit-card-icon" />
                        </div>
                        EDIT
                      </Button>
                    </Zoom>

                    <Zoom in={!formAboutDisabled}>
                      <div hidden={formAboutDisabled} className="about-buttons-container">
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          className="button save-about-button"
                          disabled={!values.backgroundImage || !values.description}
                        >
                          SAVE
                        </Button>

                        <Button
                          onClick={toggleForm}
                          variant="text"
                          color="primary"
                          className="button "
                        >
                          CANCEL
                        </Button>
                      </div>
                    </Zoom>
                  </div>
                </div>

                <p className="about-text">
                  This information will be displayed in the event invitation so players can see all
                  the details about the event - things like if cart fees and range balls are
                  included, contests, prizes, and if there's a reception. Pricing comes later so
                  don't worry about that here.
                </p>

                <div className="club-about-main-container">
                  <div className="select-picture-container">
                    <div className="about-title-container">
                      <p>CLUB PICTURE</p>
                    </div>

                    <ImagePicker
                      disabled={formAboutDisabled}
                      addPic={setValue('backgroundImage')}
                      label="UPLOAD BACKGROUND"
                      helpText="Upload a new image as a background to your club"
                      image={currentClub.backgroundImage ? currentClub.backgroundImage : undefined}
                    />
                  </div>

                  <div className="about-description-container">
                    <div className="about-title-container">
                      <p>ABOUT THE CLUB</p>
                    </div>

                    {formAboutDisabled ? (
                      <ReactMarkdown source={values.description} className="markdown" />
                    ) : (
                      <RichTextEditor
                        className="rte-editor"
                        value={text}
                        onChange={newText => {
                          setValue('description')(newText.toString('markdown'));
                          setText(newText);
                        }}
                        toolbarConfig={RTE_TOOLBARCONFIG}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </FormConsumer>
        </FormProvider>
      )}
    </Paper>
  );
};

export default AboutTheClub;
